import { useEffect } from "react";

type UseScrollLockOptions = {
    lock: boolean;
    isMobile?: boolean;
    containerSelector?: string;
};

/**
 * Locks the active scroll container while preserving and restoring previous inline styles.
 * Falls back to html/body when a container selector is not found.
 */
export default function useScrollLock({
    lock,
    isMobile = false,
    containerSelector = ".app-container",
}: UseScrollLockOptions): void {
    useEffect(() => {
        const container = document.querySelector<HTMLElement>(containerSelector);
        const html = document.documentElement;
        const body = document.body;
        const scrollTarget = container ?? html;

        const previousContainerOverflowY = container?.style.overflowY ?? "";
        const previousHtmlOverflow = html.style.overflow;
        const previousBodyOverflow = body.style.overflow;
        const previousTargetTouchAction = (scrollTarget.style as any).touchAction ?? "";
        const previousBodyTouchAction = (body.style as any).touchAction ?? "";

        if (lock) {
            if (container) {
                container.style.overflowY = "hidden";
            } else {
                html.style.overflow = "hidden";
            }
            body.style.overflow = "hidden";

            if (isMobile) {
                (scrollTarget.style as any).touchAction = "none";
                (body.style as any).touchAction = "none";
            }
        } else {
            if (container) {
                container.style.overflowY = previousContainerOverflowY;
            } else {
                html.style.overflow = previousHtmlOverflow;
            }
            body.style.overflow = previousBodyOverflow;

            if (isMobile) {
                (scrollTarget.style as any).touchAction = previousTargetTouchAction;
                (body.style as any).touchAction = previousBodyTouchAction;
            }
        }

        return () => {
            if (container) {
                container.style.overflowY = previousContainerOverflowY;
            } else {
                html.style.overflow = previousHtmlOverflow;
            }
            body.style.overflow = previousBodyOverflow;
            (scrollTarget.style as any).touchAction = previousTargetTouchAction;
            (body.style as any).touchAction = previousBodyTouchAction;
        };
    }, [lock, isMobile, containerSelector]);
}
